import { useEffect, useState, useRef, useMemo } from "react";
import { Users, Clock, Award, Heart } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
const STATS_CONFIG = [
  { target: 10, duration: 1500, icon: Clock, suffix: "+", colorClass: "from-[#003A4B] to-[#1B5E6B]" },
  { target: 500, duration: 2000, icon: Users, suffix: "+", colorClass: "from-[#C89D4B] to-[#D4B06A]" },
  { target: 98, duration: 1800, icon: Award, suffix: "%", colorClass: "from-[#003A4B] to-[#1B5E6B]" },
  { target: 24, duration: 1200, icon: Heart, suffix: "/7", colorClass: "from-[#C89D4B] to-[#D4B06A]" },
];
const useCountUp = (target, duration, startCounting) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!startCounting) return;
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) { setCount(target); clearInterval(timer); } else { setCount(Math.floor(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, startCounting]);
  return count;
};
const StatItem = ({ icon: Icon, value, label, colorClass }) => (
  <div className="text-center group">
    <div className={`w-16 h-16 bg-gradient-to-br ${colorClass} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg bg-white/10`}><Icon className="w-8 h-8 text-white" /></div>
    <p className="text-3xl md:text-4xl font-bold text-white mb-1">{value}</p>
    <p className="text-white/70 text-sm">{label}</p>
  </div>
);
export const StatsSection = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const current = ref.current;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.3 });
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);
  const count0 = useCountUp(STATS_CONFIG[0].target, STATS_CONFIG[0].duration, visible);
  const count1 = useCountUp(STATS_CONFIG[1].target, STATS_CONFIG[1].duration, visible);
  const count2 = useCountUp(STATS_CONFIG[2].target, STATS_CONFIG[2].duration, visible);
  const count3 = useCountUp(STATS_CONFIG[3].target, STATS_CONFIG[3].duration, visible);
  const counts = [count0, count1, count2, count3];
  const labels = useMemo(() => [t.stats.years, t.stats.patients, t.stats.satisfaction, t.stats.available], [t]);
  return (
    <section ref={ref} data-testid="stats-section" className="py-16 bg-[#003A4B]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS_CONFIG.map((config, i) => (<StatItem key={labels[i]} icon={config.icon} value={`${counts[i]}${config.suffix}`} label={labels[i]} colorClass={config.colorClass} />))}
        </div>
      </div>
    </section>
  );
};
